import React, { useState, useEffect, Suspense, lazy } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from './common/SafeIcon';
import MobileHeader from './components/MobileHeader';
import MobileFAB from './components/MobileFAB';
import MobileBottomSheet from './components/MobileBottomSheet';
import ShareAppButton from './components/ShareAppButton';
import CalculationTracker from './components/CalculationTracker';
import PerformanceOptimizer from './components/PerformanceOptimizer';
import SharePrompt from './components/SharePrompt';
import TutorialBanner from './components/TutorialBanner';
import InstallPrompt from './components/InstallPrompt';
import ShareWithParent from './components/ShareWithParent';
import SectionNav from './components/SectionNav';
import LazySection from './components/LazySection';
import { translations } from './lib/translations';
import { checkInstallability, showInstallPrompt, storeDeferredPrompt } from './lib/pwaUtils';

const { FiCalculator, FiCheckCircle, FiLoader, FiPrinter, FiRefreshCw, FiSmartphone, FiPercent, FiShare2, FiStar, FiX, FiUser, FiUsers, FiMonitor, FiArrowRight } = FiIcons;

// Code Splitting: Lazy load below-the-fold components to reduce initial JS payload
const SEOContent = lazy(() => import('./components/SEOContent'));
const ApplicationGuide = lazy(() => import('./components/ApplicationGuide'));
const SeniorTips = lazy(() => import('./components/SeniorTips'));
const CalculationHistory = lazy(() => import('./components/CalculationHistory'));
const SampleComputation = lazy(() => import('./components/SampleComputation'));
const FeedbackModule = lazy(() => import('./components/FeedbackModule'));
const ActivityToday = lazy(() => import('./components/ActivityToday'));

const HISTORY_KEY = 'sulit_calc_history';
const MAX_HISTORY = 3;

const LoadingFallback = ({ height = "150px" }) => (
  <div style={{ height }} className="flex items-center justify-center w-full bg-white/50 rounded-3xl border border-slate-100 shadow-sm">
    <SafeIcon icon={FiLoader} className="animate-spin text-blue-500 text-3xl" />
  </div>
);

function App() {
  const getInitialLang = () => {
    const path = window.location.pathname;
    if (path.includes('/fil')) return 'ph';
    if (path.includes('/ceb')) return 'ceb';
    return 'en';
  };

  const [lang, setLang] = useState(getInitialLang());
  const t = translations[lang];

  const isPwdOnly = window.location.pathname.includes('/pwd-discount-calculator');

  const [mode, setMode] = useState('individual');
  const [price, setPrice] = useState('');
  const [totalPeople, setTotalPeople] = useState('1');
  const [cardHolders, setCardHolders] = useState('1');
  const [isVatRegistered, setIsVatRegistered] = useState(true);
  const [hasServiceCharge, setHasServiceCharge] = useState(false);
  const [results, setResults] = useState(null);
  
  const [showGuide, setShowGuide] = useState(false);
  const [isInstallable, setIsInstallable] = useState(false);
  const [showInstallPopup, setShowInstallPopup] = useState(false);
  const [history, setHistory] = useState([]);

  const langCycle = { en: 'ph', ph: 'ceb', ceb: 'en' };
  const langLabel = { en: 'EN', ph: 'FIL', ceb: 'BIS' };

  const handleLangChange = () => {
    const nextLang = langCycle[lang];
    let basePath = isPwdOnly ? '/pwd-discount-calculator' : '';
    if (nextLang === 'en') window.location.href = basePath || '/';
    else if (nextLang === 'ph') window.location.href = basePath ? `${basePath}/fil/` : '/fil/';
    else if (nextLang === 'ceb') window.location.href = basePath ? `${basePath}/ceb/` : '/ceb/';
  };

  useEffect(() => {
    try {
      const saved = localStorage.getItem(HISTORY_KEY);
      if (saved) setHistory(JSON.parse(saved));
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    const handleBeforeInstall = (e) => {
      storeDeferredPrompt(e);
      setIsInstallable(true);
      setTimeout(() => setShowInstallPopup(true), 30000);
    };
    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    checkInstallability(setIsInstallable);
    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
  }, []);

  useEffect(() => {
    if (price && !isNaN(price)) calculate();
    else setResults(null);
  }, [price, totalPeople, cardHolders, isVatRegistered, hasServiceCharge, mode]);

  const saveToHistory = (newResults) => {
    const entry = {
      timestamp: Date.now(),
      mode,
      isVatRegistered,
      hasServiceCharge,
      price,
      totalPeople,
      cardHolders,
      results: newResults
    };
    
    setHistory(prev => {
      const updated = [entry, ...prev].slice(0, MAX_HISTORY);
      try { localStorage.setItem(HISTORY_KEY, JSON.stringify(updated)); } catch { /* ignore */ }
      return updated;
    });
  };

  const calculate = () => {
    const billPrice = parseFloat(price);
    if (isNaN(billPrice) || billPrice <= 0) return;

    let scAmount = 0;
    if (hasServiceCharge) scAmount = billPrice * 0.10;

    let discount, finalPayable;
    let vatExemptBase = billPrice;

    if (mode === 'pos') {
      discount = billPrice * 0.20;
      finalPayable = billPrice - discount + scAmount;
    } else if (mode === 'individual') {
      vatExemptBase = isVatRegistered ? billPrice / 1.12 : billPrice;
      discount = vatExemptBase * 0.20;
      finalPayable = (vatExemptBase - discount) + scAmount;
    } else {
      const pCount = parseInt(totalPeople) || 1;
      const cCount = parseInt(cardHolders) || 0;
      
      const pricePerPerson = billPrice / pCount;
      const scPerPerson = scAmount / pCount;
      
      const vatExemptPerPerson = isVatRegistered ? pricePerPerson / 1.12 : pricePerPerson;
      const discountPerPerson = vatExemptPerPerson * 0.20;
      
      const cardHolderTotal = (vatExemptPerPerson - discountPerPerson + scPerPerson) * cCount;
      const regularTotal = (pricePerPerson + scPerPerson) * (pCount - cCount);
      
      finalPayable = cardHolderTotal + regularTotal;
      discount = discountPerPerson * cCount;
    }

    const newResults = {
      originalPrice: billPrice.toFixed(2),
      discount: discount.toFixed(2),
      serviceCharge: scAmount.toFixed(2),
      billableAmount: finalPayable.toFixed(2),
      vatExemptSales: vatExemptBase.toFixed(2),
      pricePerPerson: mode === 'group' ? (finalPayable / (parseInt(totalPeople) || 1)).toFixed(2) : finalPayable.toFixed(2),
      totalPeople,
      cardHolders,
      roundedPosTotal: mode === 'pos' ? Math.round(finalPayable).toFixed(2) : undefined
    };

    setResults(newResults);
    saveToHistory(newResults);
  };

  const handleRestore = (entry) => {
    setMode(entry.mode);
    setPrice(String(entry.price));
    setIsVatRegistered(entry.isVatRegistered);
    setHasServiceCharge(entry.hasServiceCharge);
    setTotalPeople(entry.totalPeople);
    setCardHolders(entry.cardHolders);
    setResults(entry.results);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleClearHistory = () => {
    setHistory([]);
    try { localStorage.removeItem(HISTORY_KEY); } catch { /* ignore */ }
  };

  const handleReset = () => {
    setPrice('');
    setResults(null);
  };

  const handleShareCalculator = async () => {
    const shareData = {
      title: isPwdOnly ? 'Best PWD Discount Calculator — Sulit PH' : 'Best Senior & PWD Discount Calculator — Sulit PH',
      text: isPwdOnly 
        ? 'Use this free calculator for PWD discounts in the Philippines. Handles 20% discount + VAT exemption automatically!'
        : 'Use this free calculator for Senior Citizen & PWD discounts in the Philippines. Handles 20% discount + VAT exemption automatically!',
      url: window.location.origin + window.location.pathname,
    };
    
    if (navigator.share) {
      try {
        await navigator.share(shareData);
        if (window.gtag) window.gtag('event', 'share_calculator_cta', { event_category: 'engagement' });
      } catch (err) { /* cancelled */ }
    } else {
      try {
        await navigator.clipboard.writeText(`${shareData.text} ${shareData.url}`);
        alert('Calculator link copied! Share it with friends and family.');
      } catch (err) {
        alert('Please copy the URL from your browser to share.');
      }
    }
  };

  const formatCurrency = (val) => new Intl.NumberFormat('en-PH', { style: 'currency', currency: 'PHP' }).format(val);

  // Links for interlinking the different pages/versions
  const otherLinks = [
    { url: '/', label: 'Senior & PWD Calc (English)', icon: '🇵🇭', show: !(lang === 'en' && !isPwdOnly) },
    { url: '/fil/', label: 'Senior at PWD Calc (Tagalog)', icon: '🇵🇭', show: lang !== 'ph' },
    { url: '/ceb/', label: 'Senior ug PWD Calc (Cebuano)', icon: '🇵🇭', show: lang !== 'ceb' },
    { url: '/pwd-discount-calculator/', label: 'PWD-Only Calculator', icon: '♿', show: !isPwdOnly }
  ];

  return (
    <div className="min-h-screen flex flex-col max-w-md mx-auto bg-slate-50 relative pb-24 md:pb-8">
      <PerformanceOptimizer />
      <CalculationTracker results={results} discountType={mode} isVatRegistered={isVatRegistered} />

      {/* Desktop Header */}
      <header className="hidden md:block sticky top-0 z-30 bg-white/80 backdrop-blur-md border-b border-slate-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center text-white shadow-lg">
              <SafeIcon icon={FiCalculator} className="text-xl" />
            </div>
            <div>
              <h1 className="font-extrabold text-slate-900 leading-tight">{isPwdOnly ? t.pwdTitle : t.title}</h1>
              <p className="text-[10px] uppercase tracking-wider font-bold text-blue-600">Stable v1.88</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleLangChange}
              className="px-3 py-2 rounded-lg bg-blue-50 text-blue-600 font-black text-xs border border-blue-100 uppercase"
            >
              {langLabel[lang]}
            </button>
            <ShareAppButton variant="icon" isPwdOnly={isPwdOnly} />
          </div>
        </div>
      </header>

      {/* Mobile Header */}
      <MobileHeader 
        calculationMode={mode}
        onModeChange={setMode}
        onShowInfo={() => setShowGuide(true)}
        results={results}
        lang={lang}
        onLangChange={handleLangChange}
        langLabel={langLabel[lang]}
        isPwdOnly={isPwdOnly}
      />

      <main className="flex-1 px-4 py-6 md:py-8 space-y-6">
        
        {/* H1 Hero */}
        <div id="section-calculator" className="text-center pt-2 scroll-mt-20">
          <h1 className="text-3xl font-black text-slate-900 leading-tight tracking-tight">
            {isPwdOnly ? t.pwdMainHeading : t.mainHeading}
          </h1>
          <p className="text-slate-500 text-base font-medium mt-2">
            {isPwdOnly ? t.pwdDescription : t.description}
          </p>
          
          {/* Direct-answer section for SEO and quick reading */}
          <div className="mt-5 bg-white border border-slate-200 rounded-2xl p-4 text-left shadow-sm text-sm text-slate-600 leading-relaxed">
            <strong className="text-slate-800 font-black block mb-1">Quick Answer:</strong>
            {isPwdOnly 
              ? "Qualified Persons with Disability (PWDs) in the Philippines generally receive a "
              : "Qualified Senior Citizens and PWDs in the Philippines generally receive a "}
            <strong className="text-slate-800">20% discount and VAT exemption</strong> on eligible goods and services. For a VAT-inclusive qualifying price, the common formula is: <code className="bg-blue-50 text-blue-700 font-bold px-1.5 py-0.5 rounded text-xs mx-1 border border-blue-100">Final amount = (Price ÷ 1.12) × 0.80</code>.
          </div>
        </div>

        {/* Desktop Mode Toggle (Hidden on mobile) */}
        <div className="hidden md:flex bg-slate-200/50 p-1.5 rounded-2xl mb-2">
          {[
            { id: 'individual', label: t.individual, icon: FiUser },
            { id: 'group', label: t.group, icon: FiUsers },
            { id: 'pos', label: t.pos || 'POS / Resto', icon: FiMonitor }
          ].map((m) => (
            <button
              key={m.id}
              onClick={() => setMode(m.id)}
              className={`relative flex-1 py-3 px-4 rounded-xl font-bold text-sm transition-all flex items-center justify-center gap-2 ${
                mode === m.id 
                  ? 'bg-white text-blue-700 shadow-md ring-2 ring-blue-500 z-10 scale-[1.02]' 
                  : 'text-slate-500 hover:text-slate-700 hover:bg-slate-200/50'
              }`}
            >
              <SafeIcon icon={m.icon} />
              {m.label}
              {m.id === 'pos' && (
                <span className="absolute -top-2 right-2 bg-gradient-to-r from-red-500 to-rose-500 text-white text-[9px] font-black px-2 py-0.5 rounded-full shadow-sm border border-white">
                  MUST TRY
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Input Card */}
        <div className="bg-white p-5 rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-100 space-y-4">
          <div className="space-y-3">
            <label className="text-lg font-black text-slate-800 ml-1 flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center">
                <SafeIcon icon={FiCalculator} className="text-sm" />
              </div>
              {t.receiptLabel}
            </label>
            <div className="relative">
              <span className="absolute left-5 top-1/2 -translate-y-1/2 font-black text-slate-300 text-3xl">₱</span>
              <input
                type="number"
                inputMode="decimal"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                placeholder="0.00"
                className="w-full bg-slate-50 border-2 border-slate-200 rounded-2xl py-5 pl-14 pr-14 text-4xl font-black outline-none focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-500/20 transition-all text-slate-800"
              />
              {price && (
                <button 
                  onClick={() => setPrice('')}
                  className="absolute right-4 top-1/2 -translate-y-1/2 w-8 h-8 bg-slate-200 rounded-full flex items-center justify-center text-slate-600 hover:bg-slate-300 transition-colors"
                  aria-label="Clear input"
                >
                  <SafeIcon icon={FiX} className="text-lg" />
                </button>
              )}
            </div>
            
            <AnimatePresence>
              {!price && (
                <motion.div 
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="pt-2"
                >
                  <p className="text-sm font-bold text-slate-500 text-center mb-3">Want to test it? Tap an amount:</p>
                  <div className="flex justify-center gap-3">
                    {[500, 1000, 2500].map(amt => (
                      <button
                        key={amt}
                        onClick={() => setPrice(amt.toString())}
                        className="flex-1 py-3 bg-blue-50 text-blue-700 font-black rounded-xl border border-blue-100 hover:bg-blue-100 active:scale-95 transition-all text-lg"
                      >
                        ₱{amt}
                      </button>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <AnimatePresence>
            {mode === 'group' && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="grid grid-cols-2 gap-4 overflow-hidden pt-2 border-t border-slate-100"
              >
                <div className="space-y-2">
                  <label className="text-sm font-bold text-slate-600 ml-1">{t.totalDiners}</label>
                  <input
                    type="number"
                    value={totalPeople}
                    onChange={(e) => setTotalPeople(e.target.value)}
                    className="w-full bg-slate-50 border-2 border-slate-200 rounded-xl py-4 px-4 text-xl font-black outline-none focus:border-blue-500 text-center"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-bold text-slate-600 ml-1">
                    {isPwdOnly ? (t.pwdCardHolders || t.cardHolders) : t.cardHolders}
                  </label>
                  <input
                    type="number"
                    value={cardHolders}
                    onChange={(e) => setCardHolders(e.target.value)}
                    className="w-full bg-slate-50 border-2 border-slate-200 rounded-xl py-4 px-4 text-xl font-black outline-none focus:border-blue-500 text-center"
                  />
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Toggles */}
        <div className="grid grid-cols-1 gap-4">
          {mode !== 'pos' && (
            <div 
              onClick={() => setIsVatRegistered(!isVatRegistered)}
              className="flex items-center justify-between p-5 bg-white border-2 border-slate-100 rounded-3xl shadow-sm hover:border-blue-200 transition-colors cursor-pointer active:scale-[0.98]"
            >
              <div className="flex items-center gap-4">
                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center text-3xl transition-colors ${
                  isVatRegistered ? 'bg-green-100 text-green-600' : 'bg-slate-100 text-slate-400'
                }`}>
                  <SafeIcon icon={FiCheckCircle} />
                </div>
                <div>
                  <p className="text-lg font-black text-slate-900">{t.vatRegistered}</p>
                  <p className="text-xs text-slate-500 font-bold uppercase tracking-wider">12% Tax Removed</p>
                </div>
              </div>
              <div className={`w-16 h-9 rounded-full transition-colors relative pointer-events-none ${
                isVatRegistered ? 'bg-green-500' : 'bg-slate-300'
              }`}>
                <div className={`absolute top-1 w-7 h-7 bg-white rounded-full shadow-md transition-all ${
                  isVatRegistered ? 'left-8' : 'left-1'
                }`} />
              </div>
            </div>
          )}

          <div 
            onClick={() => setHasServiceCharge(!hasServiceCharge)}
            className="flex items-center justify-between p-5 bg-white border-2 border-slate-100 rounded-3xl shadow-sm hover:border-purple-200 transition-colors cursor-pointer active:scale-[0.98]"
          >
            <div className="flex items-center gap-4">
              <div className={`w-14 h-14 rounded-2xl flex items-center justify-center text-3xl transition-colors ${
                hasServiceCharge ? 'bg-purple-100 text-purple-600' : 'bg-slate-100 text-slate-400'
              }`}>
                <SafeIcon icon={FiPercent} />
              </div>
              <div>
                <p className="text-lg font-black text-slate-900">{t.serviceCharge}</p>
                <p className="text-xs text-slate-500 font-bold uppercase tracking-wider">Added to Subtotal</p>
              </div>
            </div>
            <div className={`w-16 h-9 rounded-full transition-colors relative pointer-events-none ${
              hasServiceCharge ? 'bg-purple-500' : 'bg-slate-300'
            }`}>
              <div className={`absolute top-1 w-7 h-7 bg-white rounded-full shadow-md transition-all ${
                hasServiceCharge ? 'left-8' : 'left-1'
              }`} />
            </div>
          </div>
        </div>

        {/* Results */}
        <AnimatePresence>
          {results && (
            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              className="space-y-4 pt-2"
            >
              {mode === 'pos' && (
                <div className="bg-amber-50 border-2 border-amber-200 rounded-2xl p-4 text-center">
                  <p className="text-xs font-black text-amber-600 uppercase tracking-wider flex items-center justify-center gap-2">
                    <SafeIcon icon={FiMonitor} />
                    Common POS / Restaurant Computation
                  </p>
                  <p className="text-[10px] text-amber-700 font-bold mt-1">Straight 20% discount applied to total gross bill.</p>
                </div>
              )}

              <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-3xl p-6 text-white shadow-xl shadow-green-200 text-center relative overflow-hidden">
                <div className="absolute top-0 right-0 w-40 h-40 bg-white/20 blur-3xl rounded-full -mr-16 -mt-16" />
                <p className="text-green-100 text-sm font-black uppercase tracking-widest mb-2 flex items-center justify-center gap-2">
                  <SafeIcon icon={FiStar} className="text-yellow-300" />
                  You Save This Much
                  <SafeIcon icon={FiStar} className="text-yellow-300" />
                </p>
                <h2 className="text-5xl font-black text-white drop-shadow-md mb-3">
                  ₱{results.discount}
                </h2>
                <div className="inline-block bg-black/15 px-4 py-1.5 rounded-full backdrop-blur-sm">
                  <p className="text-xs font-bold text-green-50 uppercase tracking-widest">
                    20% Discount {isVatRegistered && mode !== 'pos' ? '+ VAT Exempt' : ''}
                  </p>
                </div>
              </div>

              <div className="bg-white border-2 border-blue-100 rounded-3xl p-6 shadow-lg">
                <div className="flex justify-between items-end">
                  <div>
                    <p className="text-slate-500 text-sm font-black uppercase tracking-wider mb-1 flex items-center gap-2">
                      <SafeIcon icon={FiCheckCircle} className="text-blue-500" />
                      {t.amountToPay}
                    </p>
                    <h2 className="text-4xl font-black text-slate-900">
                      {formatCurrency(results.billableAmount)}
                    </h2>
                  </div>
                  <div className="text-right pb-1">
                    <p className="text-xs font-bold text-slate-400">Orig. Bill</p>
                    <p className="text-sm font-bold text-slate-400 line-through">₱{results.originalPrice}</p>
                  </div>
                </div>

                {mode === 'group' && (
                  <div className="mt-5 pt-5 border-t border-slate-100 bg-blue-50 -mx-6 -mb-6 p-6 rounded-b-3xl">
                    <p className="text-xs uppercase font-black text-blue-600 tracking-wider mb-1">Your Share (Per Person)</p>
                    <p className="font-black text-blue-800 text-3xl">{formatCurrency(results.pricePerPerson)}</p>
                  </div>
                )}
                
                {mode === 'pos' && (
                  <div className="mt-5 pt-5 border-t border-slate-100 bg-slate-50 -mx-6 -mb-6 p-6 rounded-b-3xl flex justify-between items-end">
                    <div>
                      <p className="text-xs uppercase font-black text-slate-500 tracking-wider mb-1">Rounded POS Total</p>
                      <p className="font-black text-slate-800 text-3xl">₱{results.roundedPosTotal}</p>
                    </div>
                    <SafeIcon icon={FiMonitor} className="text-slate-300 text-4xl mb-1" />
                  </div>
                )}
              </div>

              <ShareWithParent results={results} calculationMode={mode} isPwdOnly={isPwdOnly} />

              <div className="grid grid-cols-2 gap-3">
                <button 
                  onClick={() => window.print()}
                  className="bg-white border-2 border-slate-200 text-slate-700 py-4 rounded-2xl font-bold flex items-center justify-center gap-2 hover:bg-slate-50 active:scale-95 transition-all"
                >
                  <SafeIcon icon={FiPrinter} className="text-lg" />
                  Print
                </button>
                <button 
                  onClick={handleReset}
                  className="bg-slate-100 text-slate-600 py-4 rounded-2xl font-bold flex items-center justify-center gap-2 hover:bg-slate-200 active:scale-95 transition-all"
                >
                  <SafeIcon icon={FiRefreshCw} className="text-lg" />
                  Reset
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Deferred Render Sections via Lazy Loading & Suspense */}
        <div id="section-activity" className="scroll-mt-20">
          <LazySection minHeight="150px">
            <Suspense fallback={<LoadingFallback height="150px" />}>
              <ActivityToday history={history} isPwdOnly={isPwdOnly} />
            </Suspense>
          </LazySection>
        </div>

        <div id="section-history" className="scroll-mt-20">
          <LazySection minHeight="200px">
            <Suspense fallback={<LoadingFallback height="200px" />}>
              <CalculationHistory history={history} onRestore={handleRestore} onClear={handleClearHistory} />
            </Suspense>
          </LazySection>
        </div>

        <div id="section-feedback" className="scroll-mt-20">
          <LazySection minHeight="150px">
            <Suspense fallback={<LoadingFallback height="150px" />}>
              <FeedbackModule isPwdOnly={isPwdOnly} />
            </Suspense>
          </LazySection>
        </div>

        <LazySection minHeight="500px">
          <Suspense fallback={<LoadingFallback height="500px" />}>
            <SEOContent currentLang={lang} isPwdOnly={isPwdOnly} />
          </Suspense>
        </LazySection>

        <LazySection minHeight="500px">
          <Suspense fallback={<LoadingFallback height="500px" />}>
            <ApplicationGuide isPwdOnly={isPwdOnly} />
          </Suspense>
        </LazySection>

        <LazySection minHeight="300px">
          <Suspense fallback={<LoadingFallback height="300px" />}>
            <SampleComputation isPwdOnly={isPwdOnly} />
          </Suspense>
        </LazySection>

        <div id="section-tips" className="scroll-mt-20">
          <LazySection minHeight="250px">
            <Suspense fallback={<LoadingFallback height="250px" />}>
              <SeniorTips isPwdOnly={isPwdOnly} />
            </Suspense>
          </LazySection>
        </div>

        <div id="section-guide" className="scroll-mt-20">
          {/* ApplicationGuide is rendered above; this anchor is for nav */}
        </div>

        {/* Install + Share CTA */}
        <div className="bg-blue-600 rounded-3xl p-6 text-white text-center space-y-4 shadow-xl shadow-blue-200">
          <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto">
            <SafeIcon icon={FiSmartphone} className="text-4xl" />
          </div>
          <p className="text-base font-bold leading-tight px-4">{t.installDesc}</p>
          <motion.button 
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => showInstallPrompt()}
            className="w-full bg-white text-blue-600 py-4 rounded-2xl font-black shadow-lg uppercase tracking-tight text-lg"
          >
            {t.downloadBtn}
          </motion.button>
          
          <motion.button 
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleShareCalculator}
            className="w-full bg-white/15 border-2 border-white/40 text-white py-4 rounded-2xl font-black flex items-center justify-center gap-3 hover:bg-white/25 transition-all uppercase tracking-tight"
          >
            <SafeIcon icon={FiShare2} className="text-xl" />
            Share This Calculator
          </motion.button>
        </div>

        {/* Interlinking / Related Pages */}
        <div className="bg-slate-200/50 rounded-3xl p-5 border border-slate-200">
          <h3 className="text-center font-black text-slate-500 mb-4 text-[11px] uppercase tracking-widest">
            Other Calculators & Languages
          </h3>
          <div className="grid grid-cols-1 gap-2">
            {otherLinks.filter(l => l.show).map((link, i) => (
              <a 
                key={i} 
                href={link.url} 
                className="flex items-center justify-between p-3.5 bg-white rounded-xl border border-slate-200 hover:border-blue-300 hover:shadow-sm transition-all active:scale-[0.98] group"
              >
                <div className="flex items-center gap-3">
                  <span className="text-lg group-hover:scale-110 transition-transform">{link.icon}</span>
                  <span className="text-sm font-bold text-slate-700">{link.label}</span>
                </div>
                <SafeIcon icon={FiArrowRight} className="text-slate-400 group-hover:text-blue-500 transition-colors" />
              </a>
            ))}
          </div>
        </div>

      </main>

      <footer className="p-8 text-center bg-white border-t border-slate-100">
        <div className="font-black text-slate-800 text-lg flex items-center justify-center gap-1">
          <span className="text-blue-600 italic">Sulit</span> PH 
          <div className="w-1.5 h-1.5 bg-blue-600 rounded-full" />
        </div>
        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mt-1">
          © {new Date().getFullYear()} • Accurate PH Discount Logic
        </p>
      </footer>

      <SectionNav />
      <MobileFAB onCalculate={calculate} onReset={handleReset} onPrint={() => window.print()} results={results} />
      <TutorialBanner isPwdOnly={isPwdOnly} />
      <SharePrompt results={results} calculationMode={mode} isPwdOnly={isPwdOnly} />
      
      <AnimatePresence>
        {showInstallPopup && isInstallable && (
          <InstallPrompt 
            onInstall={showInstallPrompt} 
            onDismiss={() => setShowInstallPopup(false)} 
            title="Add to Home Screen"
            description="Use our calculator offline anytime."
          />
        )}
      </AnimatePresence>

      <MobileBottomSheet isOpen={showGuide} onClose={() => setShowGuide(false)} title="Privacy & Legal">
        <div className="p-6 space-y-4 text-base text-slate-600 font-medium">
          <p>This tool provides estimates based on {isPwdOnly ? 'RA 10754' : 'RA 9994 and RA 10754'}. Always verify with actual merchant receipts.</p>
          <div className="pt-4 border-t border-slate-100">
            <button onClick={() => setShowGuide(false)} className="w-full bg-blue-600 text-white py-4 rounded-xl font-bold text-lg">
              Close Guide
            </button>
          </div>
        </div>
      </MobileBottomSheet>

    </div>
  );
}

export default App;